import React, { FC } from "react";
import styled from "styled-components";
import SVG from "react-inlinesvg";

import Polygon from "../../assets/images/polygon.svg";
import {
  toVWMobile,
  toVWDesktop,
  toREM,
} from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";

export type CollapseMenuProps = {
  title: string;
  children: React.ReactNode;
  open: boolean;
  hoverOnly?: boolean;
  onMouseEnter: () => void;
  onTriggerClick: () => void;
};

const CollapseMenu: FC<CollapseMenuProps> = ({
  title = "",
  children,
  open,
  hoverOnly = false,
  onMouseEnter,
  onTriggerClick,
}: CollapseMenuProps) => {
  return (
    <Item
      className={`AccordionItem ${
        open ? "is-open" : "is-closed"
      }`}
      onMouseEnter={hoverOnly ? onMouseEnter : undefined}
    >
      <Header
        role="button"
        className="AccordionHeader"
        aria-expanded={open}
        tabIndex={hoverOnly ? -1 : 0}
        onClick={hoverOnly ? undefined : onTriggerClick}
        onKeyDown={(event) => {
          if (hoverOnly) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onTriggerClick();
          }
        }}
      >
        <TitleWrapper>
          <Title>{title}</Title>
          <Arrow src={Polygon} />
        </TitleWrapper>
      </Header>
      <Panel
        className="AccordionPanel"
        $open={open}
        aria-hidden={!open}
      >
        <PanelInner className="AccordionPanelInner">
          {children}
        </PanelInner>
      </Panel>
    </Item>
  );
};

const Item = styled.div`
  cursor: pointer;
`;

const Header = styled.span`
  display: block;
  width: 100%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  color: var(--color-pink-30);
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(24)}rem;
  font-weight: 900;
  font-style: normal;
  letter-spacing: ${toREM(1.44)}rem;
  line-height: normal;
  text-align: left;
  text-transform: uppercase;
  width: 90%;
  text-align: ${(props) =>
    props.theme.isRTL ? "right" : "left"};
  margin: 0;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${toREM(36)}rem;
    letter-spacing: ${toREM(1.08)}rem;
    line-height: ${toVWDesktop(40)}vw;
    width: 100%;
  }
`;

const Panel = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) =>
    $open ? "1fr" : "0fr"};
  transition: grid-template-rows 100ms ease-in;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PanelInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

export const Arrow = styled(SVG)`
  margin-left: ${toVWMobile(8)}vw;
  width: ${toVWMobile(24)}vw;
  height: ${toVWMobile(14)}vw;
  fill: var(--color-pink-30);
  z-index: 1;
  transition: transform 0.5s;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
    margin-left: ${toVWDesktop(8)}vw;
    width: ${toVWDesktop(20)}vw;
    height: ${toVWDesktop(11)}vw;
  }
`;

export default CollapseMenu;
